/**
 * Floor Plan Configurations — Site-Specific Zone Bounds & Machine Positions
 *
 * All coordinates are expressed as percentages (0–100) relative to the floor plan image,
 * with origin at TOP-LEFT (X right, Y down).
 *
 * Physical-to-Percent Conversion:
 *   For a space WxH (ft):
 *     x_pct = (x_ft / W) * 100
 *     y_pct = ((H - y_ft) / H) * 100   ← Y-flip because image y=0 is at the top
 */

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: convert physical ft coordinates to percentage for a given canvas size
// ─────────────────────────────────────────────────────────────────────────────
const ft = (x, y, W, H) => ({
    x: parseFloat(((x / W) * 100).toFixed(2)),
    y: parseFloat((((H - y) / H) * 100).toFixed(2)),
});

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULT / UNIVERSAL CONFIG (Sites: Mumbai=2, Jammu=4, Indore=5)
// Based on the original generic makerspace template
// ─────────────────────────────────────────────────────────────────────────────
export const CONFIG_UNIVERSAL = {
    image: '/makerspace_floorplan.png',
    zoneBounds: {
        'MACHINE_SHOP': {
            label: 'Machine Shop', icon: '⚙️',
            minX: 0, maxX: 100, minY: 0, maxY: 40,
            color: 'rgba(255, 152, 0, 0.15)',
        },
        'ELECTRONICS': {
            label: 'Electronics', icon: '⚡',
            minX: 0, maxX: 30, minY: 40, maxY: 70,
            color: 'rgba(33, 150, 243, 0.15)',
        },
        'WORKBENCHES': {
            label: 'Workbenches', icon: '🛠️',
            minX: 30, maxX: 75, minY: 40, maxY: 70,
            color: 'rgba(76, 175, 80, 0.15)',
        },
        'THREE_D_PRINTING': {
            label: 'Digi-Fab', icon: '🖨️',
            minX: 75, maxX: 100, minY: 40, maxY: 70,
            color: 'rgba(156, 39, 176, 0.15)',
        },
        'ADMIN': {
            label: 'Admin Desk', icon: '📋',
            minX: 0, maxX: 50, minY: 70, maxY: 100,
            color: 'rgba(233, 30, 99, 0.15)',
        },
        'CONFERENCE': {
            label: 'Meeting Room', icon: '🤝',
            minX: 50, maxX: 100, minY: 70, maxY: 100,
            color: 'rgba(0, 150, 136, 0.15)',
        },
    },
    machines: {
        'cnc':        { id: 617, name: 'CNC Router',              x: 25.0, y: 20.0, zone: 'MACHINE_SHOP' },
        'laser':      { id: 618, name: 'Laser Cutter',            x: 50.0, y: 10.0, zone: 'MACHINE_SHOP' },
        'drill':      { id: 619, name: 'Drill Press',             x: 67.5, y: 10.0, zone: 'MACHINE_SHOP' },
        'saw':        { id: 620, name: 'Band Saw',                x: 80.0, y: 10.0, zone: 'MACHINE_SHOP' },
        'grinder1':   { id: 621, name: 'Bench Grinder 1',         x: 90.0, y: 10.0, zone: 'MACHINE_SHOP' },
        'grinder2':   { id: 622, name: 'Bench Grinder 2',         x: 95.0, y: 16.0, zone: 'MACHINE_SHOP' },
        'sander':     { id: 623, name: 'Sander',                  x: 95.0, y: 20.0, zone: 'MACHINE_SHOP' },
        'e_wb1':      { id: 629, name: 'Electronics Workbench 1', x: 15.0, y: 56.0, zone: 'ELECTRONICS' },
        'e_wb2':      { id: 630, name: 'Electronics Workbench 2', x: 15.0, y: 50.0, zone: 'ELECTRONICS' },
        'e_wb3':      { id: 631, name: 'Electronics Workbench 3', x: 20.0, y: 56.0, zone: 'ELECTRONICS' },
        'e_wb4':      { id: 632, name: 'Electronics Workbench 4', x: 20.0, y: 50.0, zone: 'ELECTRONICS' },
        'h_table1':   { id: 635, name: 'Heavy Table 1',           x: 45.0, y: 56.0, zone: 'WORKBENCHES' },
        'h_table2':   { id: 636, name: 'Heavy Table 2',           x: 62.5, y: 56.0, zone: 'WORKBENCHES' },
        'printer1':   { id: 638, name: '3D Printer 1',            x: 92.5, y: 45.0, zone: 'THREE_D_PRINTING' },
        'printer2':   { id: 639, name: '3D Printer 2',            x: 92.5, y: 50.0, zone: 'THREE_D_PRINTING' },
        'printer3':   { id: 640, name: '3D Printer 3',            x: 92.5, y: 55.0, zone: 'THREE_D_PRINTING' },
        'printer4':   { id: 641, name: '3D Printer 4',            x: 92.5, y: 60.0, zone: 'THREE_D_PRINTING' },
        'filament':   { id: 642, name: 'Filament Rack',           x: 87.5, y: 64.0, zone: 'THREE_D_PRINTING' },
        'conf_table': { id: 645, name: 'Conference Table',        x: 75.0, y: 84.0, zone: 'CONFERENCE' },
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// BANGALORE CONFIG (Site ID 9)
// Physical space: 60ft wide × 33ft tall (~1980 sq ft)
// Origin: bottom-left (0,0), coordinates from user's CAD model
// ─────────────────────────────────────────────────────────────────────────────
// W=60, H=33 → x% = x/60*100, y% = (33-y)/33*100
const B = { W: 60, H: 33 };
const bft = (x, y) => ft(x, y, B.W, B.H);

export const CONFIG_BANGALORE = {
    image: '/makerspace_banglore.png',
    zoneBounds: {
        'CONFERENCE': {
            label: 'Conference Room', icon: '🤝',
            minX: 0,    maxX: 25,   minY: 0,    maxY: 54.5,
            color: 'rgba(0, 150, 136, 0.12)',
        },
        'DIGITAL_FAB': {
            label: 'Digital Fabrication', icon: '🖨️',
            minX: 25,   maxX: 58.3, minY: 15.2, maxY: 69.7,
            color: 'rgba(156, 39, 176, 0.12)',
        },
        'WOODWORKING': {
            label: 'Woodworking', icon: '🪵',
            minX: 58.3, maxX: 83.3, minY: 0,    maxY: 45.5,
            color: 'rgba(255, 152, 0, 0.12)',
        },
        'METALWORKING': {
            label: 'Metalworking & CNC', icon: '⚙️',
            minX: 83.3, maxX: 100,  minY: 0,    maxY: 45.5,
            color: 'rgba(96, 125, 139, 0.15)',
        },
        'GENERAL_ASSEMBLY': {
            label: 'General Assembly', icon: '🛠️',
            minX: 58.3, maxX: 83.3, minY: 45.5, maxY: 69.7,
            color: 'rgba(76, 175, 80, 0.12)',
        },
        'ELECTRONICS': {
            label: 'Electronics & Robotics', icon: '⚡',
            minX: 0,    maxX: 50,   minY: 69.7, maxY: 100,
            color: 'rgba(33, 150, 243, 0.12)',
        },
        'TEXTILE': {
            label: 'Textile & Sewing', icon: '🧵',
            minX: 50,   maxX: 75,   minY: 69.7, maxY: 100,
            color: 'rgba(233, 30, 99, 0.12)',
        },
        'STORAGE': {
            label: 'Storage & Supplies', icon: '📦',
            minX: 75,   maxX: 100,  minY: 69.7, maxY: 100,
            color: 'rgba(121, 85, 72, 0.12)',
        },
    },
    machines: {
        // ── Digital Fabrication Zone ──
        'printer1':     { id: 638, name: '3D Printer 1',      ...bft(20, 22), zone: 'DIGITAL_FAB' },
        'printer2':     { id: 639, name: '3D Printer 2',      ...bft(24, 22), zone: 'DIGITAL_FAB' },
        'printer3':     { id: 640, name: '3D Printer 3',      ...bft(28, 22), zone: 'DIGITAL_FAB' },
        'laser':        { id: 618, name: 'Laser Cutter',      ...bft(28, 14), zone: 'DIGITAL_FAB' },
        'vinyl_cutter': { id: 624, name: 'Vinyl Cutter',      ...bft(18, 14), zone: 'DIGITAL_FAB' },

        // ── Woodworking Zone ──
        'table_saw1':   { id: 620, name: 'Table Saw 1',       ...bft(40, 30), zone: 'WOODWORKING' },
        'table_saw2':   { id: 621, name: 'Table Saw 2',       ...bft(45, 30), zone: 'WOODWORKING' },
        'joiner':       { id: 625, name: 'Joiner',            ...bft(42, 28), zone: 'WOODWORKING' },
        'miter_saw1':   { id: 626, name: 'Miter Saw 1',       ...bft(48, 30), zone: 'WOODWORKING' },
        'miter_saw2':   { id: 627, name: 'Miter Saw 2',       ...bft(52, 30), zone: 'WOODWORKING' },
        'band_saw':     { id: 628, name: 'Band Saw',          ...bft(44, 26), zone: 'WOODWORKING' },
        'drill_press':  { id: 619, name: 'Drill Press',       ...bft(50, 26), zone: 'WOODWORKING' },
        'planer':       { id: 633, name: 'Planer',            ...bft(55, 26), zone: 'WOODWORKING' },
        'thicknesser':  { id: 634, name: 'Thicknesser',       ...bft(58, 24), zone: 'WOODWORKING' },
        'asm_table1':   { id: 635, name: 'Assembly Table 1',  ...bft(42, 22), zone: 'GENERAL_ASSEMBLY' },
        'asm_table2':   { id: 636, name: 'Assembly Table 2',  ...bft(52, 22), zone: 'GENERAL_ASSEMBLY' },

        // ── Metalworking & CNC Zone ──
        'cnc_router':   { id: 617, name: 'CNC Router',        ...bft(55, 30), zone: 'METALWORKING' },
        'lathe':        { id: 643, name: 'Lathe',             ...bft(58, 26), zone: 'METALWORKING' },
        'milling':      { id: 644, name: 'Milling Machine',   ...bft(58, 22), zone: 'METALWORKING' },
        'welding':      { id: 645, name: 'Welding Station',   ...bft(55, 18), zone: 'METALWORKING' },
        'grinder':      { id: 646, name: 'Grinder',           ...bft(58, 18), zone: 'METALWORKING' },

        // ── Electronics & Robotics Lab ──
        'solder1':      { id: 629, name: 'Soldering Station 1', ...bft(5,  5), zone: 'ELECTRONICS' },
        'solder2':      { id: 630, name: 'Soldering Station 2', ...bft(10, 5), zone: 'ELECTRONICS' },
        'solder3':      { id: 631, name: 'Soldering Station 3', ...bft(15, 5), zone: 'ELECTRONICS' },
        'oscilloscope1':{ id: 647, name: 'Oscilloscope 1',      ...bft(8,  3), zone: 'ELECTRONICS' },
        'oscilloscope2':{ id: 648, name: 'Oscilloscope 2',      ...bft(12, 3), zone: 'ELECTRONICS' },
        'func_gen':     { id: 649, name: 'Function Generator',  ...bft(18, 5), zone: 'ELECTRONICS' },
        'multimeter':   { id: 650, name: 'Multimeter Station',  ...bft(2,  5), zone: 'ELECTRONICS' },
        'pcb_printer1': { id: 651, name: 'PCB Printer 1',       ...bft(22, 5), zone: 'ELECTRONICS' },
        'pcb_printer2': { id: 652, name: 'PCB Printer 2',       ...bft(26, 5), zone: 'ELECTRONICS' },

        // ── Textile & Sewing Corner ──
        'sewing_machine':{ id: 653, name: 'Sewing Machine',   ...bft(35, 5), zone: 'TEXTILE' },
        'serger':        { id: 654, name: 'Serger',           ...bft(38, 5), zone: 'TEXTILE' },
        'cutting_table': { id: 655, name: 'Cutting Table',    ...bft(42, 5), zone: 'TEXTILE' },
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// GANDHINAGAR CONFIG (Site ID 3)
// Physical space: 80ft wide × 25ft tall (~2000 sq ft)
// Origin: bottom-left (0,0) → top-right (80, 25)
// Conversion: x% = x/80×100,  y% = (25-y)/25×100  (Y-flipped)
// ─────────────────────────────────────────────────────────────────────────────
const G = { W: 80, H: 25 };
const gft = (x, y) => ft(x, y, G.W, G.H);

export const CONFIG_GANDHINAGAR = {
    image: '/makerspace_gandhinagar.png',
    zoneBounds: {
        // ── Top Row (y: 12–25 ft → image minY: 0%, maxY: 52%) ──
        'CONFERENCE': {
            label: 'Conference Room', icon: '🤝',
            minX: 0,     maxX: 25,    minY: 0,    maxY: 60,
            color: 'rgba(0, 150, 136, 0.12)',
        },
        'ELECTRONICS_IOT': {
            label: 'Electronics & IoT', icon: '⚡',
            minX: 25,    maxX: 56.25, minY: 0,    maxY: 52,
            color: 'rgba(33, 150, 243, 0.12)',
        },
        'DIGITAL_FAB': {
            label: 'Digital Fabrication', icon: '🖨️',
            minX: 56.25, maxX: 100,   minY: 0,    maxY: 52,
            color: 'rgba(156, 39, 176, 0.12)',
        },
        // ── Bottom Row (y: 0–12 ft → image minY: 52%, maxY: 100%) ──
        'RECEPTION': {
            label: 'Reception & Storage', icon: '📋',
            minX: 0,     maxX: 25,    minY: 60,   maxY: 100,
            color: 'rgba(233, 30, 99, 0.12)',
        },
        'TEXTILE': {
            label: 'Textile & Craft', icon: '🧵',
            minX: 25,    maxX: 56.25, minY: 52,   maxY: 100,
            color: 'rgba(255, 152, 0, 0.12)',
        },
        'BREAK_AREA': {
            label: 'Break Area', icon: '☕',
            minX: 56.25, maxX: 68.75, minY: 52,   maxY: 100,
            color: 'rgba(121, 85, 72, 0.12)',
        },
        'GENERAL_WORKSTATIONS': {
            label: 'General Workstations', icon: '🛠️',
            minX: 68.75, maxX: 81.25, minY: 52,   maxY: 100,
            color: 'rgba(76, 175, 80, 0.12)',
        },
        'CNC_HEAVY': {
            label: 'CNC & Heavy Machinery', icon: '⚙️',
            minX: 81.25, maxX: 100,   minY: 52,   maxY: 100,
            color: 'rgba(96, 125, 139, 0.15)',
        },
    },
    machines: {
        // ── Digital Fabrication Zone ──
        'fdm1':       { id: 638, name: 'FDM Printer 1',       ...gft(50, 22), zone: 'DIGITAL_FAB' },
        'fdm2':       { id: 639, name: 'FDM Printer 2',       ...gft(55, 22), zone: 'DIGITAL_FAB' },
        'fdm3':       { id: 640, name: 'FDM Printer 3',       ...gft(60, 22), zone: 'DIGITAL_FAB' },
        'sla1':       { id: 641, name: 'SLA Printer 1',       ...gft(55, 18), zone: 'DIGITAL_FAB' },
        'sla2':       { id: 642, name: 'SLA Printer 2',       ...gft(60, 18), zone: 'DIGITAL_FAB' },
        'laser':      { id: 618, name: 'Laser Cutter',        ...gft(75, 18), zone: 'DIGITAL_FAB' },

        // ── Electronics & IoT Zone ──
        'solder1':    { id: 629, name: 'Soldering Station 1', ...gft(25, 18), zone: 'ELECTRONICS_IOT' },
        'solder2':    { id: 630, name: 'Soldering Station 2', ...gft(30, 18), zone: 'ELECTRONICS_IOT' },
        'solder3':    { id: 631, name: 'Soldering Station 3', ...gft(35, 18), zone: 'ELECTRONICS_IOT' },
        'osc1':       { id: 647, name: 'Oscilloscope 1',      ...gft(28, 16), zone: 'ELECTRONICS_IOT' },
        'osc2':       { id: 648, name: 'Oscilloscope 2',      ...gft(33, 16), zone: 'ELECTRONICS_IOT' },

        // ── Textile & Craft Zone ──
        'sewing1':    { id: 653, name: 'Sewing Machine 1',    ...gft(25,  5), zone: 'TEXTILE' },
        'sewing2':    { id: 654, name: 'Sewing Machine 2',    ...gft(30,  5), zone: 'TEXTILE' },
        'embroidery1':{ id: 655, name: 'Embroidery Machine 1',...gft(35,  5), zone: 'TEXTILE' },
        'embroidery2':{ id: 656, name: 'Embroidery Machine 2',...gft(38,  5), zone: 'TEXTILE' },
        'vinyl1':     { id: 624, name: 'Vinyl Cutter 1',      ...gft(32,  2), zone: 'TEXTILE' },
        'vinyl2':     { id: 625, name: 'Vinyl Cutter 2',      ...gft(40,  2), zone: 'TEXTILE' },

        // ── General Workstations ──
        'work_table1':{ id: 635, name: 'Work Table 1',        ...gft(58,  6), zone: 'GENERAL_WORKSTATIONS' },
        'work_table2':{ id: 636, name: 'Work Table 2',        ...gft(62,  6), zone: 'GENERAL_WORKSTATIONS' },

        // ── CNC & Heavy Machinery ──
        'cnc_router': { id: 617, name: 'CNC Router',          ...gft(70,  8), zone: 'CNC_HEAVY' },
        'cnc_mill':   { id: 643, name: 'Desktop CNC Mill',    ...gft(72, 10), zone: 'CNC_HEAVY' },
        'drill1':     { id: 619, name: 'Drill Press 1',       ...gft(78, 10), zone: 'CNC_HEAVY' },
        'drill2':     { id: 620, name: 'Drill Press 2',       ...gft(68,  5), zone: 'CNC_HEAVY' },
        'bandsaw1':   { id: 621, name: 'Bandsaw 1',           ...gft(75,  5), zone: 'CNC_HEAVY' },
        'bandsaw2':   { id: 622, name: 'Bandsaw 2',           ...gft(70,  5), zone: 'CNC_HEAVY' },
        'disc_sander1':{ id: 623, name: 'Disc Sander 1',      ...gft(78,  3), zone: 'CNC_HEAVY' },
        'disc_sander2':{ id: 657, name: 'Disc Sander 2',      ...gft(74,  3), zone: 'CNC_HEAVY' },
        'lathe':      { id: 644, name: 'Lathe',               ...gft(60,  2), zone: 'CNC_HEAVY' },
        'milling':    { id: 645, name: 'Milling Machine',     ...gft(62,  4), zone: 'CNC_HEAVY' },
        'welding':    { id: 646, name: 'Welding Station',     ...gft(64,  6), zone: 'CNC_HEAVY' },

        // ── Reception ──
        'reception_desk': { id: 660, name: 'Reception Desk',  ...gft(10,  5), zone: 'RECEPTION' },
        'lockers':        { id: 661, name: 'Storage Lockers', ...gft( 5,  5), zone: 'RECEPTION' },
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// JAMMU CONFIG (Site ID 4)
// Physical space: 60ft wide × 33ft tall (~1980 sq ft)
// Origin: bottom-left (0,0) → top-right (60, 33)
// Note: Structurally identical to Indore, but maintained independently for site-specific tweaks.
// ─────────────────────────────────────────────────────────────────────────────
const J = { W: 60, H: 33 };
const jft = (x, y) => ft(x, y, J.W, J.H);

export const CONFIG_JAMMU = {
    image: '/makerspace_jammu.png',
    zoneBounds: {
        'CONFERENCE': {
            label: 'Conference Room', icon: '🤝',
            minX: 0,    maxX: 25,   minY: 0,    maxY: 54.5,
            color: 'rgba(0, 150, 136, 0.12)',
        },
        'DIGITAL_FAB': {
            label: 'Digital Fabrication', icon: '🖨️',
            minX: 25,   maxX: 58.3, minY: 15.2, maxY: 69.7,
            color: 'rgba(156, 39, 176, 0.12)',
        },
        'WOODWORKING': {
            label: 'Woodworking', icon: '🪵',
            minX: 58.3, maxX: 83.3, minY: 0,    maxY: 45.5,
            color: 'rgba(255, 152, 0, 0.12)',
        },
        'METALWORKING': {
            label: 'Metalworking & CNC', icon: '⚙️',
            minX: 83.3, maxX: 100,  minY: 0,    maxY: 45.5,
            color: 'rgba(96, 125, 139, 0.15)',
        },
        'GENERAL_ASSEMBLY': {
            label: 'Assembly Area', icon: '🛠️',
            minX: 58.3, maxX: 83.3, minY: 45.5, maxY: 69.7,
            color: 'rgba(76, 175, 80, 0.12)',
        },
        'ELECTRONICS': {
            label: 'Electronics & Robotics', icon: '⚡',
            minX: 0,    maxX: 50,   minY: 69.7, maxY: 100,
            color: 'rgba(33, 150, 243, 0.12)',
        },
        'TEXTILE': {
            label: 'Textile & Sewing', icon: '🧵',
            minX: 50,   maxX: 75,   minY: 69.7, maxY: 100,
            color: 'rgba(233, 30, 99, 0.12)',
        },
        'STORAGE': {
            label: 'Storage & Supplies', icon: '📦',
            minX: 75,   maxX: 100,  minY: 69.7, maxY: 100,
            color: 'rgba(121, 85, 72, 0.12)',
        },
    },
    machines: {
        // ── Digital Fabrication Zone ──
        'printer1':     { id: 638, name: '3D Printer 1',      ...jft(22, 22), zone: 'DIGITAL_FAB' },
        'printer2':     { id: 639, name: '3D Printer 2',      ...jft(26, 22), zone: 'DIGITAL_FAB' },
        'printer3':     { id: 640, name: '3D Printer 3',      ...jft(30, 22), zone: 'DIGITAL_FAB' },
        'laser':        { id: 618, name: 'Laser Cutter',      ...jft(30, 14), zone: 'DIGITAL_FAB' },
        'vinyl_cutter': { id: 624, name: 'Vinyl Cutter',      ...jft(20, 14), zone: 'DIGITAL_FAB' },

        // ── Woodworking Zone ──
        'table_saw1':   { id: 620, name: 'Table Saw 1',       ...jft(38, 30), zone: 'WOODWORKING' },
        'table_saw2':   { id: 621, name: 'Table Saw 2',       ...jft(42, 30), zone: 'WOODWORKING' },
        'joiner':       { id: 625, name: 'Joiner',            ...jft(40, 28), zone: 'WOODWORKING' },
        'miter_saw1':   { id: 626, name: 'Miter Saw 1',       ...jft(44, 30), zone: 'WOODWORKING' },
        'miter_saw2':   { id: 627, name: 'Miter Saw 2',       ...jft(48, 30), zone: 'WOODWORKING' },
        'band_saw':     { id: 628, name: 'Band Saw',          ...jft(42, 26), zone: 'WOODWORKING' },
        'drill_press':  { id: 619, name: 'Drill Press',       ...jft(46, 26), zone: 'WOODWORKING' },
        'planer':       { id: 633, name: 'Planer',            ...jft(48, 26), zone: 'WOODWORKING' },
        'asm_table1_wd':{ id: 635, name: 'Assembly Table 1',  ...jft(40, 22), zone: 'WOODWORKING' },
        'asm_table2_wd':{ id: 636, name: 'Assembly Table 2',  ...jft(46, 22), zone: 'WOODWORKING' },

        // ── Metalworking & CNC Zone ──
        'cnc_router':   { id: 617, name: 'CNC Router',        ...jft(55, 30), zone: 'METALWORKING' },
        'lathe':        { id: 643, name: 'Lathe',             ...jft(58, 26), zone: 'METALWORKING' },
        'milling':      { id: 644, name: 'Milling Machine',   ...jft(58, 22), zone: 'METALWORKING' },
        'welding':      { id: 645, name: 'Welding Station',   ...jft(55, 18), zone: 'METALWORKING' },
        'grinder':      { id: 646, name: 'Grinder',           ...jft(58, 18), zone: 'METALWORKING' },

        // ── Electronics & Robotics Lab ──
        'solder1':      { id: 629, name: 'Soldering Station 1', ...jft( 6,  5), zone: 'ELECTRONICS' },
        'solder2':      { id: 630, name: 'Soldering Station 2', ...jft(10,  5), zone: 'ELECTRONICS' },
        'solder3':      { id: 631, name: 'Soldering Station 3', ...jft(14,  5), zone: 'ELECTRONICS' },
        'oscilloscope1':{ id: 647, name: 'Oscilloscope 1',      ...jft( 8,  3), zone: 'ELECTRONICS' },
        'oscilloscope2':{ id: 648, name: 'Oscilloscope 2',      ...jft(12,  3), zone: 'ELECTRONICS' },
        'arduino1':     { id: 649, name: 'Arduino Station 1',   ...jft(16,  5), zone: 'ELECTRONICS' },
        'arduino2':     { id: 650, name: 'Arduino Station 2',   ...jft(20,  5), zone: 'ELECTRONICS' },
        'pcb_printer1': { id: 651, name: 'PCB Printer 1',       ...jft(24,  5), zone: 'ELECTRONICS' },
        'pcb_printer2': { id: 652, name: 'PCB Printer 2',       ...jft(28,  5), zone: 'ELECTRONICS' },

        // ── Textile & Sewing Corner ──
        'sewing_machine':{ id: 653, name: 'Sewing Machine',   ...jft(35, 5), zone: 'TEXTILE' },
        'serger':        { id: 654, name: 'Serger',           ...jft(38, 5), zone: 'TEXTILE' },
        'cutting_table': { id: 655, name: 'Cutting Table',    ...jft(42, 5), zone: 'TEXTILE' },
        
        // ── Assembly Area ──
        'work_table1':   { id: 660, name: 'Collaboration Table 1', ...jft(40, 14), zone: 'GENERAL_ASSEMBLY' },
        'work_table2':   { id: 661, name: 'Collaboration Table 2', ...jft(45, 14), zone: 'GENERAL_ASSEMBLY' },
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// INDORE CONFIG (Site ID 5)
// Physical space: 60ft wide × 33ft tall (~1980 sq ft)
// Origin: bottom-left (0,0) → top-right (60, 33)
// ─────────────────────────────────────────────────────────────────────────────
const I = { W: 60, H: 33 };
const ift = (x, y) => ft(x, y, I.W, I.H);

export const CONFIG_INDORE = {
    image: '/makerspace_indore.png',
    zoneBounds: {
        'CONFERENCE': {
            label: 'Conference Room', icon: '🤝',
            minX: 0,    maxX: 25,   minY: 0,    maxY: 54.5,
            color: 'rgba(0, 150, 136, 0.12)',
        },
        'DIGITAL_FAB': {
            label: 'Digital Fabrication', icon: '🖨️',
            minX: 25,   maxX: 58.3, minY: 15.2, maxY: 69.7,
            color: 'rgba(156, 39, 176, 0.12)',
        },
        'WOODWORKING': {
            label: 'Woodworking', icon: '🪵',
            minX: 58.3, maxX: 83.3, minY: 0,    maxY: 45.5,
            color: 'rgba(255, 152, 0, 0.12)',
        },
        'METALWORKING': {
            label: 'Metalworking & CNC', icon: '⚙️',
            minX: 83.3, maxX: 100,  minY: 0,    maxY: 45.5,
            color: 'rgba(96, 125, 139, 0.15)',
        },
        'GENERAL_ASSEMBLY': {
            label: 'Assembly Area', icon: '🛠️',
            minX: 58.3, maxX: 83.3, minY: 45.5, maxY: 69.7,
            color: 'rgba(76, 175, 80, 0.12)',
        },
        'ELECTRONICS': {
            label: 'Electronics & Robotics', icon: '⚡',
            minX: 0,    maxX: 50,   minY: 69.7, maxY: 100,
            color: 'rgba(33, 150, 243, 0.12)',
        },
        'TEXTILE': {
            label: 'Textile & Sewing', icon: '🧵',
            minX: 50,   maxX: 75,   minY: 69.7, maxY: 100,
            color: 'rgba(233, 30, 99, 0.12)',
        },
        'STORAGE': {
            label: 'Storage & Supplies', icon: '📦',
            minX: 75,   maxX: 100,  minY: 69.7, maxY: 100,
            color: 'rgba(121, 85, 72, 0.12)',
        },
    },
    machines: {
        // ── Digital Fabrication Zone ──
        'printer1':     { id: 638, name: '3D Printer 1',      ...ift(22, 22), zone: 'DIGITAL_FAB' },
        'printer2':     { id: 639, name: '3D Printer 2',      ...ift(26, 22), zone: 'DIGITAL_FAB' },
        'printer3':     { id: 640, name: '3D Printer 3',      ...ift(30, 22), zone: 'DIGITAL_FAB' },
        'laser':        { id: 618, name: 'Laser Cutter',      ...ift(30, 14), zone: 'DIGITAL_FAB' },
        'vinyl_cutter': { id: 624, name: 'Vinyl Cutter',      ...ift(20, 14), zone: 'DIGITAL_FAB' },

        // ── Woodworking Zone ──
        'table_saw1':   { id: 620, name: 'Table Saw 1',       ...ift(38, 30), zone: 'WOODWORKING' },
        'table_saw2':   { id: 621, name: 'Table Saw 2',       ...ift(42, 30), zone: 'WOODWORKING' },
        'joiner':       { id: 625, name: 'Joiner',            ...ift(40, 28), zone: 'WOODWORKING' },
        'miter_saw1':   { id: 626, name: 'Miter Saw 1',       ...ift(44, 30), zone: 'WOODWORKING' },
        'miter_saw2':   { id: 627, name: 'Miter Saw 2',       ...ift(48, 30), zone: 'WOODWORKING' },
        'band_saw':     { id: 628, name: 'Band Saw',          ...ift(42, 26), zone: 'WOODWORKING' },
        'drill_press':  { id: 619, name: 'Drill Press',       ...ift(46, 26), zone: 'WOODWORKING' },
        'planer':       { id: 633, name: 'Planer',            ...ift(48, 26), zone: 'WOODWORKING' },
        'asm_table1_wd':{ id: 635, name: 'Assembly Table 1',  ...ift(40, 22), zone: 'WOODWORKING' },
        'asm_table2_wd':{ id: 636, name: 'Assembly Table 2',  ...ift(46, 22), zone: 'WOODWORKING' },

        // ── Metalworking & CNC Zone ──
        'cnc_router':   { id: 617, name: 'CNC Router',        ...ift(55, 30), zone: 'METALWORKING' },
        'lathe':        { id: 643, name: 'Lathe',             ...ift(58, 26), zone: 'METALWORKING' },
        'milling':      { id: 644, name: 'Milling Machine',   ...ift(58, 22), zone: 'METALWORKING' },
        'welding':      { id: 645, name: 'Welding Station',   ...ift(55, 18), zone: 'METALWORKING' },
        'grinder':      { id: 646, name: 'Grinder',           ...ift(58, 18), zone: 'METALWORKING' },

        // ── Electronics & Robotics Lab ──
        'solder1':      { id: 629, name: 'Soldering Station 1', ...ift( 6,  5), zone: 'ELECTRONICS' },
        'solder2':      { id: 630, name: 'Soldering Station 2', ...ift(10,  5), zone: 'ELECTRONICS' },
        'solder3':      { id: 631, name: 'Soldering Station 3', ...ift(14,  5), zone: 'ELECTRONICS' },
        'oscilloscope1':{ id: 647, name: 'Oscilloscope 1',      ...ift( 8,  3), zone: 'ELECTRONICS' },
        'oscilloscope2':{ id: 648, name: 'Oscilloscope 2',      ...ift(12,  3), zone: 'ELECTRONICS' },
        'arduino1':     { id: 649, name: 'Arduino Station 1',   ...ift(16,  5), zone: 'ELECTRONICS' },
        'arduino2':     { id: 650, name: 'Arduino Station 2',   ...ift(20,  5), zone: 'ELECTRONICS' },
        'pcb_printer1': { id: 651, name: 'PCB Printer 1',       ...ift(24,  5), zone: 'ELECTRONICS' },
        'pcb_printer2': { id: 652, name: 'PCB Printer 2',       ...ift(28,  5), zone: 'ELECTRONICS' },

        // ── Textile & Sewing Corner ──
        'sewing_machine':{ id: 653, name: 'Sewing Machine',   ...ift(35, 5), zone: 'TEXTILE' },
        'serger':        { id: 654, name: 'Serger',           ...ift(38, 5), zone: 'TEXTILE' },
        'cutting_table': { id: 655, name: 'Cutting Table',    ...ift(42, 5), zone: 'TEXTILE' },
        
        // ── Assembly Area ──
        'work_table1':   { id: 660, name: 'Work Table 1',     ...ift(40, 14), zone: 'GENERAL_ASSEMBLY' },
        'work_table2':   { id: 661, name: 'Work Table 2',     ...ift(45, 14), zone: 'GENERAL_ASSEMBLY' },
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// SITE CONFIG MAP — Lookup by Site ID (string or number)
// ─────────────────────────────────────────────────────────────────────────────
export const SITE_FLOOR_CONFIGS = {
    '2': CONFIG_UNIVERSAL,    // MakerSpace Mumbai
    '3': CONFIG_GANDHINAGAR,  // MakerSpace Gandhinagar
    '4': CONFIG_JAMMU,        // MakerSpace Jammu
    '5': CONFIG_INDORE,       // MakerSpace Indore
    '9': CONFIG_BANGALORE,    // MakerSpace Bangalore ✅ Full CAD
};

/**
 * Get the floor plan config for a given site ID.
 * Falls back to the universal config if the site is not found.
 */
export const getFloorPlanConfig = (siteId) => {
    return SITE_FLOOR_CONFIGS[String(siteId)] || CONFIG_UNIVERSAL;
};
